import { Button, Skeleton } from "antd";
import React from 'react';
import { useDispatch } from "react-redux";
import Checklist from "../../../components/Checklist";
import Icon, { ICON_NAME } from "../../../components/Icon";
import S3Image from "../../../components/S3Image";
import { openPopup2 } from "../../../contexts/PopupContext";
import { selectPermissions } from '../../../util/store/selectors';
import { useSelector } from "../../../util/store/store";
import {
    ACTION_TYPE,
    CONTRACT_PERMISSION_TYPE,
    CONTRACT_TYPE,
    USER_ROLE,
    VESSEL_TYPE,
    vesselTypes,
} from "../../../util/varibles/constants";
import { checkPermission, durationTxt, firstLetter } from "../../../util/varibles/global";
import { IChecklist, IVessel } from "../../../util/varibles/interface";
import { ACTION } from "../../../util/varibles/permission";
import ChangeAutoApi from "../../VesselManagementPage/_component/ChangeAutoApi";
import ChangeSpot from "../../VesselManagementPage/_component/ChangeSpot";
import { openEditVessel } from "../../VesselManagementPage/PopupEditVessel";
import CombineDensity from "../_component/CombineDensity";
import CostInfo from "../_component/CostInfo";
import Equipments from "../_component/Equipments";
import Storages from "../_component/Storages";
import { vesselDetailActions } from "../reducer";
import styles from "./style.module.scss";

const DetailSkeleton: React.FC = () => <div className={styles['container-detail']}>
    <div className={styles.detail}>
        <div>
            <div className={styles['component-detail']}>
                <Skeleton active title={{ width: '35%' }} paragraph={false} />
                <Skeleton.Image
                    active
                    className="!w-full !h-[200px] mt-[10px]"
                    style={{ width: '100%', height: 200 }}
                />
                <Skeleton
                    active
                    className="mt-[18px]"
                    title={false}
                    paragraph={{ rows: 4, width: ['38%', '100%', '42%', '85%'] }}
                />
            </div>
            <div className={styles['component-detail']}>
                <Skeleton active title={{ width: '38%' }} paragraph={{ rows: 1, width: ['65%'] }} />
            </div>
            <div className={styles['component-detail']}>
                <Skeleton active title={{ width: '25%' }} paragraph={{ rows: 2, width: ['70%', '55%'] }} />
            </div>
        </div>
        <div>
            <div className={styles['component-detail']}>
                <Skeleton
                    active
                    title={{ width: '24%' }}
                    paragraph={{ rows: 10, width: ['72%', '64%', '68%', '60%', '75%', '66%', '70%', '58%', '73%', '62%'] }}
                />
            </div>
            <div className={styles['component-detail']}>
                <Skeleton active title={{ width: '20%' }} paragraph={{ rows: 2, width: ['88%', '76%'] }} />
            </div>
            <div className={styles['component-detail']}>
                <Skeleton active title={{ width: '25%' }} paragraph={{ rows: 2, width: ['55%', '68%'] }} />
            </div>
        </div>
    </div>
</div>;

const Detail: React.FC = () => {
    const [canUpdateVessel] = useSelector(selectPermissions([ACTION.VESSEL.UPDATE]));
    const vessel = useSelector(state => state.vesselDetail.vessel) as IVessel;
    const vesselLive = useSelector(state => state.vesselDetail.vesselLive);
    const loading = useSelector(state => state.vesselDetail.loading.vessel);
    const roles = useSelector(state => state.login.user.roles);
    const userTenantId = useSelector(state => state.login.user.tenant_id);
    const dispatch = useDispatch();

    if (loading) return <DetailSkeleton />;
    if (!vessel || !vesselLive) return null;

    const {
        image_url,
        contact,
        width,
        length,
        speed,
        capacity,
        cleaning_time,
        harvest_capacity,
        factory_discharge,
        iot_name,
        type,
        vessel_owner,
        region_names = [],
        checklists_v2 = [],
        vessel_owner_id,
    } = vessel;

    const handleEdit = () => {
        openEditVessel({
            vessel,
            editMode: ACTION_TYPE.EDIT,
            onSave: payload => dispatch(vesselDetailActions.updateVessel(payload))
        })
    }

    const openChecklist = (checklist: IChecklist) => {
        openPopup2(<Checklist checklist={checklist} disabled={true} />, {
            modalProps: {
                title: checklist.name,
                width: '860px'
            }
        })
    }

    const { Latitude, Longitude, Speed, Heading } = vesselLive.gps || {};
    const isPermission = checkPermission([USER_ROLE.SUPER_ADMIN], roles) || (canUpdateVessel && userTenantId === vessel_owner_id);
    const isSuperAdmin = roles.includes(USER_ROLE.SUPER_ADMIN);
    const isAdmin = roles.includes(USER_ROLE.ADMIN);
    const isVesselOwner = roles.includes(USER_ROLE.VESSEL_OWNER);
    const ownerTenantId = vessel_owner_id || vessel.tenant_id;
    const isSameTenant = `${userTenantId}` === `${ownerTenantId}`;
    const hasFullSingleContract = (vessel.contracts || []).some(contract =>
        `${contract.tenant_id}` === `${userTenantId}`
        && contract.type === CONTRACT_TYPE.SINGLE
        && contract.permission === CONTRACT_PERMISSION_TYPE.FULL
    );
    const canChangeSpecialMode = isSuperAdmin || (canUpdateVessel && (
        (isAdmin && (isSameTenant || hasFullSingleContract))
        || (isVesselOwner && isSameTenant)
    ));

    return <div className={styles['container-detail']}>
        <div className={styles.detail}>
            <div>
                <div className={styles['component-detail']}>
                    <p className={'font-s1'}>Vessel image</p>
                    <S3Image {...{ imageUrl: image_url, height: 200 }} />
                    <div className="text-[15px] mt-[15px]">
                        <div className="flex items-center gap-1 mb-1">
                            <span>Auto mode:</span>
                            {canChangeSpecialMode ? <ChangeAutoApi
                                vessel={vessel}
                                onSave={enable_auto => dispatch(vesselDetailActions.updateVessel({ enable_auto }))}
                            /> : <span>{vessel.enable_auto ? 'On' : 'Off'}</span>}
                        </div>
                        <div className="text-comment">
                            Auto mode uses vessel API data to run and log operations (harvest, treatment) automatically. No manual step input is needed.
                        </div>
                    </div>
                    <div className="text-[15px] mt-[15px]">
                        <div className="flex items-center gap-1 mb-1">
                            <span>Spot mode:</span>
                            {canChangeSpecialMode ? <ChangeSpot
                                vessel={vessel}
                                onSave={enable_spot => dispatch(vesselDetailActions.updateVessel({ enable_spot }))}
                            /> : <span>{vessel.enable_spot ? 'On' : 'Off'}</span>}
                        </div>
                        <div className="text-comment">
                            Spot mode is to share vessel with other companies
                        </div>
                    </div>
                </div>
                <div className={styles['component-detail']}>
                    <p className={'font-s1'}>Vessel owner</p>
                    <div className={styles['line-info']}>
                        <span>{vessel_owner}</span>
                    </div>
                </div>
                <div className={styles['component-detail']}>
                    <p className={'font-s1'}>Region</p>
                    {region_names.map((region: any, key) => {
                        return <div key={key} className={styles['line-info']}>
                            <span>{region}</span>
                        </div>
                    })}
                </div>
            </div>
            <div>
                <div className={styles['component-detail']}>
                    <div className={styles['component-detail-header']}>
                        <span className={'font-s1'}>Vessel info.</span>
                        {isPermission && <div className='flex justify-end'>
                            <Button className='bt-ghost' onClick={handleEdit} title='Edit'>
                                <Icon icon={ICON_NAME.EDIT} />
                            </Button>
                        </div>}
                    </div>
                    <div>
                        <div className={styles['line-info']}>
                            <span>Vessel type:</span>
                            <span>{vesselTypes[type - 1]?.name}</span>
                        </div>
                        <div className={styles['line-info']}>
                            <span>Contact number:</span>
                            <span>{contact}</span>
                        </div>
                        <div className={styles['line-info']}>
                            <span>Width:</span>
                            <span>{width} meters</span>
                        </div>
                        <div className={styles['line-info']}>
                            <span>Length:</span>
                            <span>{length} meters</span>
                        </div>
                        <div className={styles['line-info']}>
                            <span>Speed:</span>
                            <span>{speed} knots</span>
                        </div>
                        {type !== VESSEL_TYPE.SERVICE && <>
                            <div className={styles['line-info']}>
                                <span>Capacity:</span>
                                <span>{Math.round(capacity / 1000) / 1000} tons</span>
                            </div>
                            <div className={styles['line-info']}>
                                <span>Harvesting Speed:</span>
                                <span>{harvest_capacity} fish/hour</span>
                            </div>
                            <div className={styles['line-info']}>
                                <span>Discharging speed:</span>
                                <span>{factory_discharge} ton/hour</span>
                            </div>
                        </>}
                        <div className={styles['line-info']}>
                            <span>Cleaning time:</span>
                            <span>{durationTxt(cleaning_time || 0)}</span>
                        </div>
                        <div className={styles['line-info']}>
                            <span>OPV serial number:</span>
                            <span>{iot_name}</span>
                        </div>
                        <CostInfo
                            vessel={vessel}
                            onSave={payload => dispatch(vesselDetailActions.updateVessel(payload))}
                        />
                        {vessel.type !== VESSEL_TYPE.SERVICE && <Storages vessel={vessel} />}
                        {vessel.type === VESSEL_TYPE.SERVICE && <div className='mt-1'>
                            <span className='font-[600]'>Service equipment:</span>
                            <span className='flex gap-2 mt-[5px]'>
                                {(vessel.capabilities || []).map(item => <div
                                    key={item.id}
                                    className='box-color-main h-[32px]'
                                >
                                    {firstLetter(item.name)}
                                </div>)}
                            </span>
                        </div>}
                        {vessel.type !== VESSEL_TYPE.PROCESS_VESSEL && <Equipments vessel={vessel} />}
                        {vessel.type === VESSEL_TYPE.WELL_BOAT && <CombineDensity vessel={vessel} />}
                    </div>
                </div>
                <div className={styles['component-detail']}>
                    <p className={'font-s1'}>Checklists</p>
                    <div className={styles.checklists}>
                        {(checklists_v2 || []).map(data => <div
                            key={data.id}
                            className={styles.checklist}
                            onClick={() => openChecklist(data)}
                        >
                            {data.name}
                        </div>)}
                    </div>
                </div>
                <div className={styles['component-detail']}>
                    <p className={'font-s1'}>Current position</p>
                    <div className={styles['line-info']}>
                        <span>Speed/heading:</span>
                        <span>{Math.round(Speed * 100) / 100} knots / {Math.round(Heading)}&#xb0;</span>
                    </div>
                    <div className={styles['line-info']}>
                        <span>Latitude/ Longitude:</span>
                        <span>{Longitude?.toFixed(6)}/ {Latitude?.toFixed(6)}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>;
};

export default Detail;
